// src/context/AuthContext.js
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import { apiFetch, getAuthToken } from "../apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => getAuthToken());
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem("authUser");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Validate token on mount / when token changes
    useEffect(() => {
        if (!token) return;

        let cancelled = false;

        const validate = async () => {
            setLoading(true);
            try {
                const data = await apiFetch("/api/auth/me");
                const u = data.user || data;

                if (!cancelled) {
                    setUser(u);
                    localStorage.setItem("authUser", JSON.stringify(u));
                }
            } catch (err) {
                console.warn("Token validation failed:", err);
                if (!cancelled) {
                    // If token is bad, clear it once so you don’t get stuck in a loop
                    localStorage.removeItem("authToken");
                    localStorage.removeItem("authUser");
                    setToken(null);
                    setUser(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        validate();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const login = useCallback(async (email, password) => {
        setLoading(true);
        setError(null);
        try {
            const data = await apiFetch("/api/auth/login", {
                method: "POST",
                body: JSON.stringify({ email, password }),
            });

            localStorage.setItem("authToken", data.token);
            localStorage.setItem("authUser", JSON.stringify(data.user));

            setToken(data.token);
            setUser(data.user);

            // Legacy keys some of your pages still expect:
            localStorage.setItem("email", data.user.email);
            localStorage.setItem("trader_id", data.user.email);

            return data;
        } catch (err) {
            console.error("Login failed:", err);
            setError(err.message || "Login failed");
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
        localStorage.removeItem("email");
        localStorage.removeItem("trader_id");
        setToken(null);
        setUser(null);
    }, []);

    const value = {
        token,
        user,
        userId: user?.id,          // numeric DB id
        userEmail: user?.email,    // email – this is what trade_settings uses
        loading,
        error,
        isLoggedIn: !!token,
        login,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return ctx;
}
